import { Language } from '../types';

const STORAGE_KEY_MANUAL = 'sorena_user_chosen_lang';
const STORAGE_KEY_DETECTED = 'sorena_detected_lang';

/**
 * Heuristic 1: Inspect URL query parameter (?lang=en or ?lang=fa)
 */
export function getLangFromUrl(): Language | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const langParam = params.get('lang')?.toLowerCase();
  if (langParam === 'en' || langParam === 'fa') {
    return langParam;
  }
  return null;
}

/**
 * Heuristic 2: Get user manually chosen language from localStorage
 */
export function getSavedManualLang(): Language | null {
  if (typeof window === 'undefined') return null;
  try {
    const saved = localStorage.getItem(STORAGE_KEY_MANUAL);
    if (saved === 'en' || saved === 'fa') {
      return saved;
    }
  } catch {
    // Ignore storage restrictions
  }
  return null;
}

/**
 * Save user explicit choice
 */
export function setSavedManualLang(lang: Language): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY_MANUAL, lang);
    sessionStorage.setItem(STORAGE_KEY_DETECTED, lang);
  } catch {
    // Ignore storage restrictions
  }
}

/**
 * Heuristic 3: Check Timezone and Browser Languages
 * If user is on a VPN (e.g., Europe, US, UK), the timezone or IP reflects that.
 */
export function getBrowserHeuristicLang(): Language {
  if (typeof window === 'undefined') return 'fa';

  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    // If timezone is specifically Iran, default to Persian
    if (timeZone.toLowerCase().includes('tehran') || timeZone.toLowerCase().includes('iran')) {
      return 'fa';
    }

    // Check navigator languages
    const navLangs = navigator.languages || [navigator.language || ''];
    const hasFa = navLangs.some((l) => l.toLowerCase().startsWith('fa'));
    const isForeignTz = timeZone && !timeZone.toLowerCase().includes('tehran');

    // If timezone is foreign (e.g. Amsterdam, London, New York, Frankfurt) and no explicit Persian primary
    if (isForeignTz && !navLangs[0]?.toLowerCase().startsWith('fa')) {
      return 'en';
    }

    if (hasFa) {
      return 'fa';
    }
  } catch {
    // fallback
  }

  return 'fa';
}

/**
 * Asynchronous Geo-IP detection:
 * 1. Calls internal /api/detect-lang (which reads CF-IPCountry and X-Forwarded-For)
 * 2. Fallbacks to ultra-fast public endpoint https://api.country.is
 * If country is NOT 'IR' (e.g. user with VPN in NL, US, DE, UK, etc.), returns 'en'.
 */
export async function detectGeoIpLanguage(): Promise<Language> {
  // If user already manually chose language, do not override
  const manual = getSavedManualLang();
  if (manual) return manual;

  // Check if already detected this session
  try {
    const sessionLang = sessionStorage.getItem(STORAGE_KEY_DETECTED);
    if (sessionLang === 'en' || sessionLang === 'fa') {
      return sessionLang;
    }
  } catch {
    // Ignore
  }

  // Attempt 1: Internal API
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 1800);
    const res = await fetch('/api/detect-lang', { signal: controller.signal });
    clearTimeout(timeoutId);
    if (res.ok) {
      const data = await res.json();
      if (data.country) {
        const lang: Language = data.country.toUpperCase() === 'IR' ? 'fa' : 'en';
        sessionStorage.setItem(STORAGE_KEY_DETECTED, lang);
        return lang;
      }
    }
  } catch {
    // Try external fallback
  }

  // Attempt 2: Fast public Geo-IP (https://api.country.is)
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);
    const res = await fetch('https://api.country.is', { signal: controller.signal });
    clearTimeout(timeoutId);
    if (res.ok) {
      const data = await res.json();
      const country = data.country ? data.country.toUpperCase() : '';
      if (country) {
        // Any country other than Iran gets English (international standard)
        const lang: Language = country === 'IR' ? 'fa' : 'en';
        sessionStorage.setItem(STORAGE_KEY_DETECTED, lang);
        return lang;
      }
    }
  } catch {
    // Fallback to browser heuristic
  }

  return getBrowserHeuristicLang();
}
